import { useState, useEffect } from "react"
import {
  Users,
  UserPlus,
  Edit,
  Trash2,
  Eye,
  Search,
  Filter,
  X,
  CheckCircle,
  XCircle,
  Key,
  Building2,
  Calendar,
  Mail,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  AlertCircle,
  UserCheck,
  UserX,
} from "lucide-react"
import { HRManagerService, type HRManager, type HRManagerType, type HRManagerFilters, type HRManagerStats } from "../services/hr-manager.service"
import { HRService } from "../services/hr.service"

interface HRAccountManagementProps {
  onError?: (error: string) => void
  onSuccess?: (message: string) => void
}

/**
 * Extract user-friendly error message from API error
 */
const getErrorMessage = (error: any): string => {
  // Handle Axios errors
  if (error?.response) {
    const status = error.response.status
    const data = error.response.data

    // Handle validation errors (400)
    if (status === 400) {
      if (data?.errors && Array.isArray(data.errors)) {
        return data.errors.join('. ') || data.message || "Please check your input and try again"
      }
      if (data?.message) {
        return data.message
      }
      return "Invalid request. Please check your input and try again"
    }

    // Handle authentication errors (401)
    if (status === 401) {
      return "Your session has expired. Please log in again"
    }

    // Handle authorization errors (403)
    if (status === 403) {
      return data?.message || "You don't have permission to perform this action"
    }

    // Handle not found errors (404)
    if (status === 404) {
      return data?.message || "The requested resource was not found"
    }

    // Handle server errors (500+)
    if (status >= 500) {
      return "Server error. Please try again later or contact support"
    }

    // Generic error message from response
    if (data?.message) {
      return data.message
    }

    if (data?.error) {
      return data.error
    }
  }

  // Handle network errors
  if (error?.message) {
    if (error.message.includes('Network Error') || error.message.includes('timeout')) {
      return "Network error. Please check your connection and try again"
    }
    return error.message
  }

  // Fallback
  return "An unexpected error occurred. Please try again"
}

export default function HRAccountManagement({ onError, onSuccess }: HRAccountManagementProps) {
  const [hrManagers, setHrManagers] = useState<HRManager[]>([])
  const [organizations, setOrganizations] = useState<any[]>([])
  const [stats, setStats] = useState<HRManagerStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [organizationsLoading, setOrganizationsLoading] = useState(false)
  const [statsLoading, setStatsLoading] = useState(false)
  
  // Error and success messages
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  
  // Filters
  const [filters, setFilters] = useState<HRManagerFilters>({
    page: 1,
    limit: 10,
  })
  const [showFilters, setShowFilters] = useState(false)
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false)
  const [selectedHRManager, setSelectedHRManager] = useState<HRManager | null>(null)
  
  // Form data
  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    hrManagerType: "" as HRManagerType | "",
    organizationId: "",
  })
  
  // Loading states
  const [creating, setCreating] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [resettingPassword, setResettingPassword] = useState(false)
  const [formError, setFormError] = useState("")
  
  // Helper to show error
  const showError = (message: string) => {
    setErrorMessage(message)
    setSuccessMessage(null)
    onError?.(message)
    // Auto-hide after 5 seconds
    setTimeout(() => setErrorMessage(null), 5000)
  }
  
  // Helper to show success
  const showSuccess = (message: string) => {
    setSuccessMessage(message)
    setErrorMessage(null)
    onSuccess?.(message)
    // Auto-hide after 3 seconds
    setTimeout(() => setSuccessMessage(null), 3000)
  }
  
  // HR Manager Types
  const hrManagerTypes: Array<{ value: HRManagerType; label: string; description: string }> = [
    { value: "PAYROLL", label: "Payroll", description: "Full access to all HR features including payroll and pay reports" },
    { value: "OPERATIONS", label: "Operations", description: "Access to all features except pay reports" },
    { value: "ONBOARDING", label: "Onboarding", description: "Access to agent assignment and agent status management" },
    { value: "ONBOARDING_ASSISTANT", label: "Onboarding Assistant", description: "Limited access to agent status management only" },
    { value: "QUALITY_CONTROL", label: "Quality Control", description: "Access to quality control features only" },
  ]
  
  // Load organizations
  const loadOrganizations = async () => {
    try {
      setOrganizationsLoading(true)
      const result = await HRService.getOrganizations()
      setOrganizations(Array.isArray(result) ? result : [])
    } catch (error) {
      console.error("Error loading organizations:", error)
      const errorMessage = getErrorMessage(error)
      showError(errorMessage)
      setOrganizations([])
    } finally {
      setOrganizationsLoading(false)
    }
  }
  
  // Load HR Managers
  const loadHRManagers = async () => {
    try {
      setLoading(true)
      const result = await HRManagerService.getHRManagers({
        ...filters,
        page: currentPage,
      })
      setHrManagers(result.hrManagers)
      setTotal(result.pagination.total)
      setTotalPages(result.pagination.totalPages)
    } catch (error: any) {
      console.error("Error loading HR Managers:", error)
      const errorMessage = getErrorMessage(error)
      showError(errorMessage)
      setHrManagers([])
    } finally {
      setLoading(false)
    }
  }
  
  // Load stats
  const loadStats = async () => {
    try {
      setStatsLoading(true)
      const result = await HRManagerService.getHRManagerStats()
      setStats(result)
    } catch (error) {
      console.error("Error loading stats:", error)
      // Stats loading failure is not critical, so we don't show error to user
    } finally {
      setStatsLoading(false)
    }
  }
  
  useEffect(() => {
    loadOrganizations()
    loadStats()
  }, [])
  
  useEffect(() => {
    loadHRManagers()
  }, [currentPage, filters])
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [filters.search, filters.hrManagerType, filters.organizationId, filters.isActive])
  
  // Handle create
  const handleCreate = async () => {
    if (!formData.email || !formData.firstName || !formData.lastName || !formData.hrManagerType) {
      setFormError("Please fill in all required fields")
      return
    }
    
    setCreating(true)
    setFormError("")
    
    try {
      await HRManagerService.createHRManager({
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        hrManagerType: formData.hrManagerType as HRManagerType,
        organizationId: formData.organizationId || undefined,
      })
      
      showSuccess("HR Manager created successfully")
      setShowAddModal(false)
      resetForm()
      loadHRManagers()
      loadStats()
    } catch (error: any) {
      console.error("Error creating HR Manager:", error)
      const errorMessage = getErrorMessage(error)
      setFormError(errorMessage)
      showError(errorMessage)
    } finally {
      setCreating(false)
    }
  }
  
  // Handle update
  const handleUpdate = async () => {
    if (!selectedHRManager || !formData.firstName || !formData.lastName) {
      setFormError("Please fill in all required fields")
      return
    }
    
    setUpdating(true)
    setFormError("")
    
    try {
      await HRManagerService.updateHRManager(selectedHRManager.id, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        hrManagerType: formData.hrManagerType as HRManagerType || undefined,
        organizationId: formData.organizationId || null,
      })
      
      showSuccess("HR Manager updated successfully")
      setShowEditModal(false)
      resetForm()
      loadHRManagers()
      loadStats()
    } catch (error: any) {
      console.error("Error updating HR Manager:", error)
      const errorMessage = getErrorMessage(error)
      setFormError(errorMessage)
      showError(errorMessage)
    } finally {
      setUpdating(false)
    }
  }
  
  // Handle delete
  const handleDelete = async () => {
    if (!selectedHRManager) return
    
    setDeleting(true)
    
    try {
      await HRManagerService.deleteHRManager(selectedHRManager.id)
      showSuccess("HR Manager deleted successfully")
      setShowDeleteModal(false)
      loadHRManagers()
      loadStats()
    } catch (error: any) {
      console.error("Error deleting HR Manager:", error)
      const errorMessage = getErrorMessage(error)
      showError(errorMessage)
    } finally {
      setDeleting(false)
    }
  }
  
  // Handle toggle status
  const handleToggleStatus = async (hrManager: HRManager) => {
    try {
      await HRManagerService.toggleHRManagerStatus(hrManager.id, !hrManager.isActive)
      showSuccess(`HR Manager ${hrManager.isActive ? "deactivated" : "activated"} successfully`)
      loadHRManagers()
      loadStats()
    } catch (error: any) {
      console.error("Error toggling HR Manager status:", error)
      const errorMessage = getErrorMessage(error)
      showError(errorMessage)
    }
  }
  
  // Handle reset password
  const handleResetPassword = async () => {
    if (!selectedHRManager) return
    
    setResettingPassword(true)
    
    try {
      await HRManagerService.resetHRManagerPassword(selectedHRManager.id)
      showSuccess("Password reset email sent successfully")
      setShowResetPasswordModal(false)
    } catch (error: any) {
      console.error("Error resetting password:", error)
      const errorMessage = getErrorMessage(error)
      showError(errorMessage)
    } finally {
      setResettingPassword(false)
    }
  }
  
  // Reset form
  const resetForm = () => {
    setFormData({
      email: "",
      firstName: "",
      lastName: "",
      hrManagerType: "",
      organizationId: "",
    })
    setFormError("")
    setSelectedHRManager(null)
  }
  
  // Open edit modal
  const openEditModal = (hrManager: HRManager) => {
    setSelectedHRManager(hrManager)
    setFormData({
      email: hrManager.email,
      firstName: hrManager.firstName,
      lastName: hrManager.lastName,
      hrManagerType: hrManager.hrManagerType || "",
      organizationId: hrManager.organizationId || "",
    })
    setShowEditModal(true)
  }
  
  // Open view modal
  const openViewModal = (hrManager: HRManager) => {
    setSelectedHRManager(hrManager)
    setShowViewModal(true)
  }
  
  // Open delete modal
  const openDeleteModal = (hrManager: HRManager) => {
    setSelectedHRManager(hrManager)
    setShowDeleteModal(true)
  }
  
  // Open reset password modal
  const openResetPasswordModal = (hrManager: HRManager) => {
    setSelectedHRManager(hrManager)
    setShowResetPasswordModal(true)
  }
  
  // Get HR Manager type label
  const getHRManagerTypeLabel = (type: HRManagerType | null) => {
    if (!type) return "Unassigned"
    return hrManagerTypes.find(t => t.value === type)?.label || type
  }
  
  // Get account age display
  const getAccountAgeDisplay = (age: number) => {
    if (age < 1) {
      const months = Math.floor(age * 12)
      return `${months} month${months !== 1 ? "s" : ""}`
    }
    return `${age.toFixed(1)} year${age !== 1 ? "s" : ""}`
  }
  
  return (
    <div className="space-y-6">
      {/* Error Message Banner */}
      {errorMessage && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-sm font-medium text-red-800">{errorMessage}</p>
          </div>
          <button
            onClick={() => setErrorMessage(null)}
            className="text-red-500 hover:text-red-700 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {/* Success Message Banner */}
      {successMessage && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
            <p className="text-sm font-medium text-green-800">{successMessage}</p>
          </div>
          <button
            onClick={() => setSuccessMessage(null)}
            className="text-green-500 hover:text-green-700 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">HR Account Management</h2>
          <p className="text-gray-600 mt-1">Manage HR Manager accounts, permissions, and organization assignments</p>
        </div>
        <button
          onClick={() => {
            resetForm()
            setShowAddModal(true)
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <UserPlus className="w-5 h-5" />
          <span>Add HR Manager</span>
        </button>
      </div>
      
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-600">Total HR Managers</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-2xl font-bold text-green-600">{stats.active}</div>
            <div className="text-sm text-gray-600">Active</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-2xl font-bold text-orange-600">{stats.inactive}</div>
            <div className="text-sm text-gray-600">Inactive</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-2xl font-bold text-gray-600">{stats.unassignedType}</div>
            <div className="text-sm text-gray-600">Unassigned Type</div>
          </div>
        </div>
      )}
      
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">HR Managers</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Filter className="w-4 h-4" />
              Filters
            </button>
          </div>
        </div>
        
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                value={filters.search || ""}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder="Search by name or email..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">HR Manager Type</label>
              <select
                value={filters.hrManagerType || ""}
                onChange={(e) => setFilters({ ...filters, hrManagerType: e.target.value || undefined })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                {hrManagerTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Organization</label>
              <select
                value={filters.organizationId || ""}
                onChange={(e) => setFilters({ ...filters, organizationId: e.target.value || undefined })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={organizationsLoading}
              >
                <option value="">All Organizations</option>
                {organizations.map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.isActive === undefined ? "" : filters.isActive ? "true" : "false"}
                onChange={(e) => setFilters({ ...filters, isActive: e.target.value === "" ? undefined : e.target.value === "true" })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
        )}
        
        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">HR Manager Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organization</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account Age</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                    Loading HR Managers...
                  </td>
                </tr>
              ) : hrManagers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                    No HR Managers found
                  </td>
                </tr>
              ) : (
                hrManagers.map((hrManager) => (
                  <tr key={hrManager.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {hrManager.firstName} {hrManager.lastName}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                      {hrManager.email}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        hrManager.hrManagerType 
                          ? "bg-blue-100 text-blue-800" 
                          : "bg-gray-100 text-gray-800"
                      }`}>
                        {getHRManagerTypeLabel(hrManager.hrManagerType)}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                      {hrManager.organization?.name || "-"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${
                        hrManager.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}>
                        {hrManager.isActive ? (
                          <>
                            <CheckCircle className="w-3 h-3" />
                            Active
                          </>
                        ) : (
                          <>
                            <XCircle className="w-3 h-3" />
                            Inactive
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                      {getAccountAgeDisplay(hrManager.accountAge)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openViewModal(hrManager)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openEditModal(hrManager)}
                          className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(hrManager)}
                          className={`p-2 rounded-lg transition-colors ${
                            hrManager.isActive
                              ? "text-orange-600 hover:bg-orange-50"
                              : "text-green-600 hover:bg-green-50"
                          }`}
                          title={hrManager.isActive ? "Deactivate" : "Activate"}
                        >
                          {hrManager.isActive ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => openResetPasswordModal(hrManager)}
                          className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                          title="Reset Password"
                        >
                          <Key className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openDeleteModal(hrManager)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Showing {((currentPage - 1) * (filters.limit || 10)) + 1} to {Math.min(currentPage * (filters.limit || 10), total)} of {total} results
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Add HR Manager</h3>
                <button
                  onClick={() => {
                    setShowAddModal(false)
                    resetForm()
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {formError}
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">HR Manager Type *</label>
                  <select
                    value={formData.hrManagerType}
                    onChange={(e) => setFormData({ ...formData, hrManagerType: e.target.value as HRManagerType | "" })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Type</option>
                    {hrManagerTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                  {formData.hrManagerType && (
                    <p className="text-xs text-gray-500 mt-1">
                      {hrManagerTypes.find(t => t.value === formData.hrManagerType)?.description}
                    </p>
                  )}
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Organization (Optional)</label>
                  <select
                    value={formData.organizationId}
                    onChange={(e) => setFormData({ ...formData, organizationId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={organizationsLoading}
                  >
                    <option value="">No Organization</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAddModal(false)
                  resetForm()
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {creating && <Loader2 className="w-4 h-4 animate-spin" />}
                Create HR Manager
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Modal */}
      {showEditModal && selectedHRManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Edit HR Manager</h3>
                <button
                  onClick={() => {
                    setShowEditModal(false)
                    resetForm()
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {formError}
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">HR Manager Type</label>
                  <select
                    value={formData.hrManagerType}
                    onChange={(e) => setFormData({ ...formData, hrManagerType: e.target.value as HRManagerType | "" })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Unassigned</option>
                    {hrManagerTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                  {formData.hrManagerType && (
                    <p className="text-xs text-gray-500 mt-1">
                      {hrManagerTypes.find(t => t.value === formData.hrManagerType)?.description}
                    </p>
                  )}
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
                  <select
                    value={formData.organizationId}
                    onChange={(e) => setFormData({ ...formData, organizationId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={organizationsLoading}
                  >
                    <option value="">No Organization</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowEditModal(false)
                  resetForm()
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                disabled={updating}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {updating && <Loader2 className="w-4 h-4 animate-spin" />}
                Update HR Manager
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* View Modal */}
      {showViewModal && selectedHRManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">HR Manager Details</h3>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
                  <p className="text-sm font-medium text-gray-900">
                    {selectedHRManager.firstName} {selectedHRManager.lastName}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                  <p className="text-sm text-gray-900">{selectedHRManager.email}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">HR Manager Type</label>
                  <p className="text-sm text-gray-900">
                    {getHRManagerTypeLabel(selectedHRManager.hrManagerType)}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Organization</label>
                  <p className="text-sm text-gray-900">
                    {selectedHRManager.organization?.name || "No Organization"}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${
                    selectedHRManager.isActive
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}>
                    {selectedHRManager.isActive ? (
                      <>
                        <CheckCircle className="w-3 h-3" />
                        Active
                      </>
                    ) : (
                      <>
                        <XCircle className="w-3 h-3" />
                        Inactive
                      </>
                    )}
                  </span>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Account Age</label>
                  <p className="text-sm text-gray-900">
                    {getAccountAgeDisplay(selectedHRManager.accountAge)}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Created At</label>
                  <p className="text-sm text-gray-900">
                    {new Date(selectedHRManager.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Last Login</label>
                  <p className="text-sm text-gray-900">
                    {selectedHRManager.lastLoginAt 
                      ? new Date(selectedHRManager.lastLoginAt).toLocaleDateString()
                      : "Never"}
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowViewModal(false)
                  openEditModal(selectedHRManager)
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Modal */}
      {showDeleteModal && selectedHRManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Delete HR Manager</h3>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to delete <strong>{selectedHRManager.firstName} {selectedHRManager.lastName}</strong>? 
                This will deactivate their account.
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Reset Password Modal */}
      {showResetPasswordModal && selectedHRManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Reset Password</h3>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to reset the password for <strong>{selectedHRManager.firstName} {selectedHRManager.lastName}</strong>? 
                A temporary password will be sent to their email.
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowResetPasswordModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleResetPassword}
                disabled={resettingPassword}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {resettingPassword && <Loader2 className="w-4 h-4 animate-spin" />}
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
